import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import novtiaIcon from "../../assets/logos/novtia-icon.svg";
import useTheme from "../../hooks/useTheme";

const navItems = [
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/labs", label: "Labs" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label="Toggle color theme"
      className="inline-flex items-center justify-center h-8 w-8 border border-line-strong text-ink hover:border-signal hover:text-signal transition-colors"
    >
      {theme === "dark" ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          className="h-4 w-4"
        >
          <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.8 6.8 0 0 0 10.5 10.5Z" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          className="h-4 w-4"
        >
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7" />
        </svg>
      )}
    </button>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 navbar-blur">
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5">
          <img src={novtiaIcon} alt="NOVTIA icon" className="h-7 w-7" />
          <span className="font-mono font-medium tracking-[0.22em] text-xs text-ink">
            NOVTIA
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <ul className="flex items-center gap-7 text-sm">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `pb-0.5 border-b transition-colors ${
                      isActive
                        ? "text-ink border-signal"
                        : "text-muted border-transparent hover:text-ink hover:border-line-strong"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <ThemeToggle />
          <Link
            to="/contact"
            className="inline-flex items-center bg-signal px-4 py-1.5 text-xs font-semibold text-on-signal hover:bg-signal-strong transition-colors"
          >
            Start a conversation
          </Link>
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-3 md:hidden">
          <ThemeToggle />
          <button
            className="inline-flex items-center justify-center h-8 w-8 border border-line-strong text-ink"
            onClick={() => setOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            <span className="sr-only">Toggle navigation</span>
            <div className="space-y-1">
              <span className="block w-4 h-px bg-ink" />
              <span className="block w-4 h-px bg-ink" />
              <span className="block w-4 h-px bg-ink" />
            </div>
          </button>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="absolute top-16 inset-x-0 md:hidden bg-surface border-b border-line">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-sm ${
                      isActive || location.pathname === item.to
                        ? "bg-surface-2 text-signal"
                        : "text-ink hover:bg-surface-2"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block bg-signal text-center px-3 py-2 text-sm font-semibold text-on-signal mt-2"
              >
                Start a conversation
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
